import React from 'react';
import { Card, Descriptions, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getCalendarEventById } from '@/api/calendar';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';


interface EventTabProps {
  eventId: string;
}

const EventTab: React.FC<EventTabProps> = ({ eventId }) => {
  const { t } = useTranslation(['calendar', 'common']);
  const { firestoreUser } = useAuth();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['calendarEvent', eventId],
    queryFn: () => getCalendarEventById(eventId, firestoreUser!),
    enabled: !!eventId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !event) {
    return (
      <Card>
        <Empty description={t('calendar:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('calendar:fields.title')}>
          {event.title}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('calendar:fields.description')}>
          {event.description}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('calendar:fields.type')}>
          {t(`calendar:types.${event.type}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('calendar:fields.start')}>
          {dayjs(event.start).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('calendar:fields.end')}>
          {dayjs(event.end).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        
        {event.hostedBy && (
          <Descriptions.Item label={t(`calendar:fields.${event.hostedBy.type}`)}>
            {event.hostedBy.name}
          </Descriptions.Item>
        )}
        
        {event.attendees && event.attendees.length > 0 && (
          <Descriptions.Item label={t('calendar:fields.attendees')}>
            {event.attendees.map(attendee => attendee.name).join(', ')}
          </Descriptions.Item>
        )}
        
        {event.notes && (
          <Descriptions.Item label={t('calendar:fields.notes')}>
            {event.notes}
          </Descriptions.Item>
        )}
        
        {event.status && (
          <Descriptions.Item label={t('calendar:fields.status')}>
            {t(`calendar:status.${event.status}`)}
          </Descriptions.Item>
        )}
        
        {event.isAllDay && (
          <Descriptions.Item label={t('calendar:fields.isAllDay')}>
            {t('common:yes')}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default EventTab; 